import { gql } from "@apollo/client";

/**
 * Event Insights — the Events page "Insights" tab. Maps to the api's
 * eventInsights.resolver.ts (a port of the member tools' Zone Performance view).
 */

/**
 * Zone × Event-Type participation for a quarter (+ this month, + prior quarter).
 *
 * Fields are inlined rather than shared via a fragment on purpose: the admin's
 * Apollo cache runs with `addTypename: false`, so a type-conditioned fragment
 * (`... on ZonePeriodModel`) can't be matched and its fields get silently
 * dropped. Inlining avoids that.
 */
const ZONE_PERIOD_FIELDS = `
  key
  label
  zones {
    zone
    totals {
      events
      rsvps
      showed
      showUpRate
    }
    types {
      type
      typeLabel
      events
      rsvps
      showed
      noShowEvents
      showUpRate
      avgRsvps
      share
    }
  }
`;

export const ADMIN_ZONE_PERFORMANCE = gql`
  query adminZonePerformance($quarter: String) {
    adminZonePerformance(quarter: $quarter) {
      today
      zoneOrder
      isCurrent
      selectedQuarter
      quarters {
        value
        label
      }
      month { ${ZONE_PERIOD_FIELDS} }
      quarter { ${ZONE_PERIOD_FIELDS} }
      prevQuarter { ${ZONE_PERIOD_FIELDS} }
    }
  }
`;

/** The events behind one matrix cell (a zone × event-type for a period). */
export const ADMIN_ZONE_PERFORMANCE_EVENTS = gql`
  query adminZonePerformanceEvents(
    $period: String!
    $zone: String!
    $eventType: String
    $quarter: String
  ) {
    adminZonePerformanceEvents(
      period: $period
      zone: $zone
      eventType: $eventType
      quarter: $quarter
    ) {
      zone
      eventType
      eventTypeLabel
      periodLabel
      count
      totalRsvps
      totalAttended
      noAttendance
      events {
        eventId
        title
        type
        typeLabel
        whenUtc
        host
        rsvps
        attended
        showRate
      }
    }
  }
`;

/** Table 1 — current members by colour zone and tenure, adjusted + raw. */
const MEMBER_ZONE_MATRIX_FIELDS = `
  zones
  grandTotal
  zoneTotals {
    zone
    count
  }
  rows {
    tenure
    tenureLabel
    total
    counts {
      zone
      count
    }
  }
`;

export const ADMIN_MEMBER_ZONE_MATRIX = gql`
  query adminMemberZoneMatrix {
    adminMemberZoneMatrix {
      adjusted { ${MEMBER_ZONE_MATRIX_FIELDS} }
      raw { ${MEMBER_ZONE_MATRIX_FIELDS} }
    }
  }
`;

/** Table 2 — upcoming events, and where members are, by timezone. */
export const ADMIN_EVENT_TIMEZONE_SUPPLY = gql`
  query adminEventTimezoneSupply($days: Int) {
    adminEventTimezoneSupply(days: $days) {
      days
      hostTypes {
        key
        label
      }
      regions {
        offset
        region
        label
        members
      }
      events {
        eventId
        title
        host
        hostType
        whenUtc
        utcMin
        startMs
        appLink
      }
    }
  }
`;

/** Drill-down for one timezone-supply region (display only — no email). */
export const ADMIN_TIMEZONE_REGION_MEMBERS = gql`
  query adminTimezoneRegionMembers($offset: Float!, $limit: Int) {
    adminTimezoneRegionMembers(offset: $offset, limit: $limit) {
      offset
      label
      truncated
      members {
        name
        country
        timezone
        level
      }
    }
  }
`;
